import moment from 'moment';

/**
 * Single source of truth for every traveler-facing Watchdog string.
 * Keeping the copy here (rather than inline in JSX) makes the wording
 * reviewable in one place and ready for i18n extraction later.
 */

export const MONITORING_MAX_DAYS = 10;
export const TRAVEL_MAX_DAYS = 10;
export const MONITORING_LEAD_DAYS = 3;
export const SWEEP_MIN_HOURS = 12;
export const SWEEP_MAX_HOURS = 24;

export const WATCHDOG_COPY = {
	navTab: 'Watchdog',
	cardTitle: 'Trip Watchdog',
	cardSubtitle:
		'Significant news and developing events at your destination — monitored by a swarm of Safe-xplore AI agents.',
	infoTooltip:
		"The Watchdog is Safe-xplore's event-monitoring service. AI agents sweep hyperlocal news and official sources every 12–24 hours during your monitoring window and report only events significant to travelers on site.",

	feedTitle: 'Findings',

	// State A — no travel dates set
	noDatesHeadline: 'Add your travel dates',
	noDatesBody:
		"Your dates tell the Watchdog what matters: it prioritizes events and disruptions likely to still affect conditions while you're there. Set them to enable monitoring for this trip.",
	noDatesCta: 'Set travel dates',

	// State B — travel dates modal
	datesDialogTitle: 'Your travel dates',
	datesDialogHelper: 'The Watchdog uses these dates to judge what’s relevant to you.',
	datesDialogStartLabel: 'Start date',
	datesDialogEndLabel: 'End date',
	datesDialogCancel: 'Cancel',
	datesDialogSave: 'Save dates',
	datesDialogRangeHint: `Trips can be up to ${TRAVEL_MAX_DAYS} days long.`,
	datesSavedToast: 'Travel dates saved — you can now start a Watchdog for this trip.',
	datesDialogError: 'Something went wrong, please try again.',

	// State C — dates set, Watchdog not started
	notMonitoringChip: 'Not monitoring',
	startHeadline: 'Start a Watchdog for this trip',
	howItWorks: [
		{
			icon: 'event_available',
			title: '1 · Pick your monitoring window',
			body: 'Monitoring runs up to 10 days per report. Most travelers start about three days before departure — early enough to catch strikes, closures, and alerts with time to adjust plans — and run it through the end of the trip.',
		},
		{
			icon: 'radar',
			title: '2 · Agents sweep every 12–24 hours',
			body: '',
		},
		{
			icon: 'inbox',
			title: '3 · Findings arrive in batches',
			body: 'New findings appear here after each sweep and are emailed to you. First results typically land within the hour. A batch can come back empty — that means the sweep completed and nothing met the reporting bar.',
		},
	],
	monitoringWindowLabel: 'Monitoring window',
	monitoringWindowHelper:
		'Up to 10 days. Recommended: three days before departure through your return date.',
	startCta: 'Start Watchdog',

	// State D — first sweep pending
	activeChip: 'Watchdog active',
	pendingHeadline: 'First sweep underway',

	// State E — sweep complete, no findings
	noFindingsHeadline: 'No significant events found',

	// State F — monitoring window ended
	endedChip: 'Monitoring ended',

	// Disclaimer (§8)
	disclaimerHeader: 'About Watchdog findings',
	disclaimerBody:
		'Watchdog reports are generated by AI agents monitoring public and official sources. We filter aggressively for significance and relevance, but no monitoring service sees everything: coverage depends on what local sources publish, when, and how accurately. Events may be missed, delayed, or reported incompletely, and conditions on the ground can change faster than any sweep cycle. Treat findings as decision support for planning and awareness. They complement — and never replace — your own good judgment and situational awareness on site, which remain your best protection anywhere.',
	disclaimerOneLiner:
		'Watchdog findings support your planning; your judgment and situational awareness on site remain your best protection.',

	// Microcopy inventory (§9)
	searchPlaceholder: 'Search findings…',
	emptySearchResult: 'No findings match your search.',
	allCategories: 'All categories',
	clearFilters: 'Clear filters',
	sortLabel: 'Sort',
	categoryLabel: 'Category',
};

export const SORT_OPTIONS = [
	{ value: 'severity', label: 'Severity' },
	{ value: 'newest', label: 'Newest first' },
	{ value: 'category', label: 'Category' },
];

export const DEFAULT_SORT = 'severity';

/** "Aug 17–21, 2026" / "Aug 28 – Sep 3, 2026" / "Dec 28, 2026 – Jan 3, 2027" */
export function formatDateRange(from?: string | Date | null, to?: string | Date | null): string {
	if (!from || !to) {
		return '';
	}
	const start = moment(from);
	const end = moment(to);
	if (!start.isValid() || !end.isValid()) {
		return '';
	}
	if (start.isSame(end, 'year')) {
		if (start.isSame(end, 'month')) {
			return `${start.format('MMM D')}–${end.format('D')}, ${end.format('YYYY')}`;
		}
		return `${start.format('MMM D')} – ${end.format('MMM D')}, ${end.format('YYYY')}`;
	}
	return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
}

/** "Aug 17" — short, local, no year noise. */
export function formatShortDate(value?: string | Date | null): string {
	if (!value) {
		return '';
	}
	const date = moment(value);
	return date.isValid() ? date.format('MMM D') : '';
}

/** "Aug 17, 2026 at 6:40 PM" — used for the last-sweep stamp. */
export function formatDateTime(value?: string | Date | null): string {
	if (!value) {
		return '';
	}
	const date = moment(value);
	return date.isValid() ? date.format('MMM D, YYYY [at] h:mm A') : '';
}

export function intro(destination: string): string {
	return `Your Risk Ratings and Risk Brief cover the baseline in ${destination} — the risks that hold true in any week of the year. The Watchdog covers your week. It deploys a swarm of specialized AI agents that monitor hyperlocal news and official sources for health, safety, security, transportation, and logistics developments that could affect you while you're there.`;
}

export function sweepBlockBody(destination: string): string {
	return `Each sweep scours local news, transit authorities, and official channels, then applies strict relevance filters. Only events with plausible significance to travelers in ${destination} make your report.`;
}

export function emailFinePrint(email?: string | null): string {
	return email ? `Findings are also sent to ${email}.` : 'Findings are also sent to your inbox.';
}

export function pendingBodyPrimary(destination: string): string {
	return `Your Watchdog's agents are searching local news and official sources for ${destination} now. First findings usually post within the hour and will also reach your inbox.`;
}

export function pendingBodySecondary(endDate: string): string {
	return `A sweep can come back with nothing to report. That's a result: it means no significant health, safety, security, or transportation events met the reporting bar. Monitoring continues either way, with sweeps every 12–24 hours through ${endDate}.`;
}

export function pendingMetaRow(start: string, end: string, email?: string | null): string {
	const tail = email ? ` · Findings emailed to ${email}` : '';
	return `Monitoring ${start} – ${end} · Sweeps every 12–24 hours${tail}`;
}

export function noFindingsBody(sweepTime: string, nextSweepHours: number): string {
	return `The latest sweep finished ${sweepTime} and found nothing affecting your travel window that met the reporting bar. Quiet is a finding — we don't pad reports to look busy. The next sweep runs within ${nextSweepHours} hours.`;
}

export function monitoringEndedLine(endedOn: string): string {
	return `This Watchdog completed its window on ${endedOn}. Findings above remain part of your report.`;
}

export function feedStatusLine(lastSweep: string, nextSweepHours: number): string {
	if (!lastSweep) {
		return `Next sweep within ${nextSweepHours} hours`;
	}
	return `Last sweep completed ${lastSweep} · Next sweep within ${nextSweepHours} hours`;
}

export function resultCountLine(shown: number, total: number): string {
	if (shown === total) {
		return total === 1 ? '1 finding' : `${total} findings`;
	}
	return `Showing ${shown} of ${total} findings`;
}

/** Notification / email subjects from the §9 microcopy inventory. */
export const notificationCopy = {
	bell: (destination: string) => `Your ${destination} Watchdog: new findings`,
	emailSubject: (count: number, destination: string, dates: string) =>
		`Watchdog: ${count} new findings for your ${destination} trip (${dates})`,
	emailSubjectEmpty: (destination: string) =>
		`Watchdog: all quiet in ${destination} — sweep complete`,
};
